import React, { useState } from "react";
import axios from "axios";
import { API_BASE_URL, API_KEY } from "../config.js";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import HotelandResort from "../assets/Icons/categories/hotel&resort.svg";
import Pension from "../assets/Icons/categories/pension.svg";
import Apartment from "../assets/Icons/categories/apartment.svg";
import PrivateRoom from "../assets/Icons/categories/privateroom.svg";
import Warehouses from "../assets/Icons/categories/warehouse.svg";
import SharedSpace from "../assets/Icons/categories/shared-space.svg";
import Hall from "../assets/Icons/categories/hall.svg";
import GuestHouse from "../assets/Icons/categories/guest-house.svg";
import Other from "../assets/Icons/categories/other.svg";

import FilterModal from "./FilterModal.jsx";

import "./SearchHouse.css";

const SearchHouse = ({ onFilter }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState({
    isNearbySearch: false,
    location: { latitude: null, longitude: null },
    selectedCity: "Addis Ababa",
    category: "",
    selectedHouseType: "Hotel and Resort",
    selectedPriceRange: { min: 0, max: 100000 },
  });
  const [showModal, setShowModal] = useState(false);
  const [notificationCount, setNotificationCount] = useState(0);
  const [fetchedTotalCount, setFetchedTotalCount] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const ITEMS_PER_PAGE = 15;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const initData = window.Telegram.WebApp.initData;

  const fetchFilteredData = async () => {

    try {
      const {
        selectedCity,
        selectedPriceRange,
        isNearbySearch,
        location,
        category,
      } = filters; // values from the central `filters` state

      const apiFilters = {
        city: selectedCity || null,
        minPrice: selectedPriceRange?.min || null,
        maxPrice: selectedPriceRange?.max || null,
        latitude: isNearbySearch ? location.latitude : null,
        longitude: isNearbySearch ? location.longitude : null,
        category: category || null,
        range: isNearbySearch ? 10 : null,
        page_size: ITEMS_PER_PAGE,
      };

      // Remove null values from apiFilters
      const activeFilters = Object.fromEntries(
        Object.entries(apiFilters).filter(([_, v]) => v !== null)
      );

      const response = await axios.post(
        `${API_BASE_URL}/user/api/v1/filter/`,
        activeFilters,
        {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": API_KEY,
            "X-Telegram-InitData": initData,
          },
        }
      );

      const data = response.data || {};
      const totalCount = data.count || 0;

      console.log("SearchHouse component rendered");
      console.log("Selected data:", data);
      console.log("Total count:", totalCount);
      console.log("Fetched total count:", fetchedTotalCount);

      setNotificationCount(totalCount);

      return { results: data.results || [], totalCount };
    } catch (error) {
      console.error("Error fetching filtered results:", error);
      setNotificationCount(0);
      return { results: [], totalCount: 0 };
    }
  };

 const handleNearbySearchToggle = () => {
   setFilters((prevFilters) => {
     const newIsNearbySearch = !prevFilters.isNearbySearch;

     if (newIsNearbySearch && navigator.geolocation) {
       navigator.geolocation.getCurrentPosition(
         (position) => {
           setFilters((prev) => ({
             ...prev,
             location: {
               latitude: position.coords.latitude,
               longitude: position.coords.longitude,
             },
             selectedCity: prev.selectedCity, // Keep selected city
           }));
         },
         (error) => {
           console.error("Error getting location:", error);
         }
       );
     } else {
       // If disabling nearby search, reset location
       return {
         ...prevFilters,
         isNearbySearch: newIsNearbySearch,
         location: { latitude: null, longitude: null },
         selectedCity: prevFilters.selectedCity, // Keep city
       };
     }

     return { ...prevFilters, isNearbySearch: newIsNearbySearch }; // Ensuring state toggles immediately
   });
 };


  const handleCategoryClick = (selectedCategory, label) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      category: selectedCategory,
      selectedHouseType: label,
    }));
  };

  const handleMinPriceChange = (min) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      selectedPriceRange: { ...prevFilters.selectedPriceRange, min },
    }));
  };

  const handleMaxPriceChange = (max) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      selectedPriceRange: {
        ...prevFilters.selectedPriceRange,
        max: Math.min(max, 3000),
      },
    }));
  };

  const handleCityChange = (e) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      selectedCity: e.target.value,
    }));
  };

  const handleApplyFilters = async () => {
    if (!fetchedTotalCount) {
      // First fetch total count only
      const { totalCount } = await fetchFilteredData();
      setFetchedTotalCount(totalCount);
    } else {
      // If count is already fetched, fetch full results
      const { results, totalCount } = await fetchFilteredData();
      // setFilteredResults(results);
      setShowResults(true);

      navigate("/filtered-results", {
        state: {
          results,
          totalCount,
          filters: {
            ...filters,
            ITEMS_PER_PAGE,
          },
        },
      });
    }
  };

  const handleSearchChange = (e) => {
    const newTerm = e.target.value;
    setSearchTerm(newTerm);
    if (onFilter) {
      onFilter(newTerm);
    }
  };

  const handleFilterClick = () => {
    setShowModal(true);
  };

  const resetFilters = () => {
    setFilters({
      isNearbySearch: false,
      location: { latitude: null, longitude: null },
      selectedCity: "",
      category: "",
      selectedHouseType: "Hotel and Resort",
      selectedPriceRange: { min: 0, max: 100000 },
    });
    setSearchTerm(""); 
  };

  const iconData = [
    {
      label: "Private Compound",
      icon: PrivateRoom,
      category: "private compound",
    },
    {
      label: "Hotel and Resort",
      icon: HotelandResort,
      category: "Hotel and Resort",
    },
    { label: "Pension", icon: Pension, category: "pension" },
    { label: "Apartment", icon: Apartment, category: "Apartment" },
    { label: "Warehouses", icon: Warehouses, category: "warehouse" },
    {
      label: "Shared space office",
      icon: SharedSpace,
      category: "shared space office",
    },
    { label: "Hall", icon: Hall, category: "Hall" },
    { label: "Guest House", icon: GuestHouse, category: "guest house" },
    { label: "Other", icon: Other, category: "Other" },
  ];

  return (
    <>
      <div className="search-container flex justify-center w-full px-4 md:px-8 lg:px-12">
        <div className="search-wrapper flex items-center gap-2 md:gap-4 w-full max-w-md">
          <div className="relative flex-grow">
            <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
              <svg
                className="w-4 h-4 text-gray-500 dark:text-gray-400"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 20 20"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z"
                />
              </svg>
            </div>
            <input
              type="text"
              placeholder={t("Search any type of house")}
              className="search-input w-full py-8 h-24 px-4 rounded-lg border-none outline-none text-sm bg-gray-200 dark:bg-gray-700"
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          <div
            className="relative filter-icon cursor-pointer"
            onClick={handleFilterClick}
          >
            <FilterListOutlinedIcon className="w-6 h-6 text-black-600 dark:text-black-300" />
            {notificationCount > 0 && (
              <span className="absolute top-0 right-0 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                {notificationCount}
              </span>
            )}
          </div>
        </div>

        <FilterModal
          showModal={showModal}
          onClose={() => setShowModal(false)}
          onApplyFilters={handleApplyFilters}
          totalCount={fetchedTotalCount}
          filters={filters}
          setFilters={setFilters}
          handleMinPriceChange={handleMinPriceChange}
          handleMaxPriceChange={handleMaxPriceChange}
          handleCityChange={handleCityChange}
          handleNearbySearchToggle={handleNearbySearchToggle}
          handleCategoryClick={handleCategoryClick}
          resetFilters={resetFilters}
          showResults={showResults}
          iconData={iconData}
        />
      </div>
    </>
  );
};

export default SearchHouse;
